/**
 * @file        src/pages/hotel/BookingDetailsDrawer.jsx
 * @description Rich detail view for a hotel booking (Booking List → Details).
 *              Fetches GET /hotel/bookings/:id/details — OUR authoritative amounts
 *              + the supplier-enriched stay / rooms / per-room guests / cancellation
 *              policy proxied from the customer booking record — plus deep links to
 *              the customer voucher/invoice and Update / Cancel actions. Mirrors the
 *              flight BookingDetailsDrawer.
 * @author      Kinetika Labs
 * @created     2026-07-16
 * @changelog
 *   - 2026-10-09 Claude — Full-screen button beside the close X opens these details as
 *                          their own page in a new tab (`page` prop renders without
 *                          the modal overlay — route /hotel/bookings/:id/details).
 *   - 2026-10-08 Claude — Convenience-fee / Discount lines print the admin-given rule names (fare.convenience_label / fare.discount_label); generic label as fallback.
 *   - 2026-10-06 Claude — Price Summary: Base fare includes the processing fee; Convenience fee in its place.
 *                          Client decision: the Processing fee (our markup) is not
 *                          a line of its own — it is inside the Base fare, and the
 *                          Convenience fee (shown even at ₹0) takes its place.
 *                          Display only: no total or stored figure changes.
 *   - 2026-10-06 Claude — Status chip prints cells.statusLabel (Booked → Success,
 *                          unsettled → Pending), the word the storefront prints.
 *   - 2026-09-27 Claude — Status chip uses the list's shared statusTone (cells.jsx);
 *                          its own copy coloured On Hold / Hold Expired green here
 *                          and amber on the list. Shows TripJack's own status under
 *                          ours when the two disagree (details `supplier_status`),
 *                          and says so when a hold reads Hold Expired because its
 *                          deadline passed before the sweep recorded it.
 *   - 2026-09-23 Claude — Takes the screen's `audience` so its document links
 *                          record where they were opened from (lib/docOrigin) —
 *                          a corporate booking's invoice then keeps the
 *                          Corporate sidebar group open instead of the B2C one.
 *   - 2026-09-10 Claude — Stay: the property now identifies itself in ONE block
 *                          (name + star rating + address) instead of the address
 *                          hanging below the whole grid, and the star rating the
 *                          API already returned is finally rendered. PAN dropped
 *                          from the guest lines (client rule — invoice only).
 *   - 2026-08-04 Claude — CR D2: added a "Share by Email" action opening
 *                          ShareEmailModal (Voucher/Invoice/Both, real PDF
 *                          attachment, logged).
 *   - 2026-08-04 Claude — Flight parity: Amendments card (list + Raise via
 *                          RaiseHotelAmendmentsModal) and the Timeline card
 *                          (booking_logs history via components/BookingTimeline).
 *   - 2026-07-16 Claude — Initial (flight-parity hotel Details drawer).
 *   - 2026-08-10 Claude — Price Summary always itemises base / taxes /
 *                          processing / convenience (a zero line reads ₹0.00
 *                          instead of vanishing — QA read the hidden lines as
 *                          missing data), and the Invoice / Voucher / Share
 *                          actions are gated by docAccess(): none while
 *                          pending or cancellation-pending, all when
 *                          confirmed, invoice only when cancelled.
 *   - 2026-09-25 Claude — The drawer is a FIXED box that scrolls inside
 *                          (`.lk-modal.lk-tall` + one `.lk-modal-body` wrapping all
 *                          three states) and its loading state is a DrawerSkeleton
 *                          shaped like the body, not a one-line "Loading…". It used
 *                          to open at that line's height and jump when the data
 *                          landed — measured 244px → 1,410px on this drawer — then
 *                          again as the amendments and timeline queries answered
 *                          separately. Now the frame never resizes once the body is
 *                          at the viewport ceiling, so a late query moves nothing.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import Icon from '../../components/Icon';
import FullScreenButton from '../../components/FullScreenButton';
import DrawerSkeleton from '../../components/DrawerSkeleton';
import Chip from '../../components/Chip';
import BookingTimeline from '../../components/BookingTimeline';
import RefundPipeline from '../../components/RefundPipeline';
import ShareEmailModal from '../../components/ShareEmailModal';
import { getBookingDetails, listAmendments } from '../../api/hotelBookings';
import { docPath } from '../../lib/docOrigin';
import { docAccess, statusTone, statusLabel } from './cells';
import RaiseHotelAmendmentsModal from './RaiseHotelAmendmentsModal';

const inr2 = (n) =>
  `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function dt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: true,
    day: '2-digit', month: 'short', year: 'numeric',
  });
}
function day(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
// TripJack's order status → the stored statuses that AGREE with it. Only a
// known status that matches none of them is flagged; an unfamiliar one is not
// guessed at. A lapsed hold agrees with ON_HOLD (the sweep has not run yet) and
// with CANCELLED / FAILED (the sweep cancels it at TripJack).
const SUPPLIER_AGREES = {
  SUCCESS: ['Booked'],
  CANCELLED: ['Cancelled', 'Refunded', 'Hold Expired'],
  CANCELED: ['Cancelled', 'Refunded', 'Hold Expired'],
  CANCELLATION_PENDING: ['Cancellation Requested'],
  ON_HOLD: ['On Hold', 'Hold Expired'],
  UNCONFIRMED: ['On Hold', 'Hold Expired'],
  FAILED: ['Failed', 'Hold Expired'],
  PENDING: ['Pending', 'Awaiting confirmation', 'Initiated'],
};
const supplierDisagrees = (ours, theirs) => {
  const agrees = SUPPLIER_AGREES[String(theirs || '').toUpperCase()];
  return Boolean(agrees) && !agrees.includes(ours);
};
const guestName = (t, i) =>
  `${t.title ? `${t.title}. ` : ''}${[t.firstName, t.lastName].filter(Boolean).join(' ')}`.trim()
  || `Guest ${i + 1}`;

function Row({ label, children }) {
  return (
    <div><span className="muted">{label}</span><div>{children ?? <span className="muted">—</span>}</div></div>
  );
}

// Turn a stored amendment type (DATE_CHANGE / …) into a readable label.
function prettyType(t) {
  if (!t) return '—';
  if (/^SSR$/i.test(t)) return 'Special Service Request';
  return String(t).toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function BookingDetailsDrawer({ bookingId, audience = 'b2c', onClose, onUpdate, onCancel, page = false }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['hotel-booking-details', bookingId],
    queryFn: () => getBookingDetails(bookingId),
    enabled: !!bookingId,
  });
  const b = data?.data;
  const canCancel = b && /book|confirm|success/i.test(b.booking_status || '') && !/cancel|refund/i.test(b.booking_status || '');
  const docs = docAccess(b?.booking_status);
  const fare = b?.fare || {};
  // Supplier star rating (TripJack `rt`, else the synced hotels row's rating),
  // clamped to a printable 0–5 so a stray supplier value can't draw 40 stars.
  const stars = Math.min(5, Math.max(0, Math.round(Number(b?.hotel?.stars) || 0)));
  // `always` keeps the line visible at ₹0.00 — the standard charge lines must
  // always itemise (a silently absent Processing fee reads as missing data).
  const feeLine = (label, v, always) => ((always || Number(v))
    ? <div className="det-fare-row"><span>{label}</span><span>{inr2(v)}</span></div>
    : null);

  const [raiseOpen, setRaiseOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const amendmentsQ = useQuery({
    queryKey: ['hotel-booking-amendments', bookingId],
    queryFn: () => listAmendments(bookingId),
    enabled: !!bookingId,
  });
  const amendments = amendmentsQ.data?.data || [];

  return (
    <>
    <div className={page ? 'booking-page' : 'lk-overlay'} onMouseDown={(e) => !page && e.target === e.currentTarget && onClose()}>
      <div className="lk-modal lk-tall panel glass" style={page ? { padding: 24 } : { maxWidth: 760, width: '96%' }}>
        <div className="lk-modal-head">
          <h3>Hotel Booking Details {b ? `· #${b.id}` : ''}</h3>
          <span style={{ display: 'inline-flex', gap: 8 }}>
            {!page ? <FullScreenButton product="hotel" bookingId={bookingId} audience={audience} /> : null}
            <button className="ib" aria-label="Close" onClick={onClose}><Icon name="x" /></button>
          </span>
        </div>

        <div className="lk-modal-body">
        {isLoading ? <DrawerSkeleton cells={6} blocks={2} />
          : isError || !b ? <div className="empty">Could not load booking.</div>
          : (
          <div className="det-body">
            {/* Summary */}
            <div className="det-grid">
              <Row label="Booking Ref"><b className="mono">{b.booking_reference || '—'}</b></Row>
              <Row label="Status">
                <Chip tone={statusTone(b.booking_status)}>{statusLabel(b.booking_status) || '—'}</Chip>
                {b.hold_lapsed ? (
                  <div className="muted" style={{ fontSize: '.85em' }}>Pay-by deadline passed — the expiry sweep will record it.</div>
                ) : null}
                {supplierDisagrees(b.booking_status, b.supplier_status) ? (
                  <div className="muted" style={{ fontSize: '.85em' }}>
                    TripJack says <b>{b.supplier_status}</b> — our record updates on the next sync.
                  </div>
                ) : null}
              </Row>
              <Row label="Customer">{b.customer_email || '—'} {b.is_guest ? <Chip tone="warn">Guest</Chip> : null}</Row>
              <Row label="Booked On">{dt(b.created_at)}</Row>
            </div>

            {/* Stay — the property identifies itself in ONE block (name, star
                rating, address). The address used to hang below the whole grid,
                detached from the Hotel cell it belongs to, and the star rating
                the API already returns was never rendered at all. */}
            <h4 className="det-h">Stay</h4>
            <div className="det-hotel">
              <div className="det-hotel-name">
                <b>{b.hotel?.name || b.stay?.hotelName || '—'}</b>
                {stars ? <span className="det-stars" title={`${stars} star`}>{'★'.repeat(stars)}</span> : null}
              </div>
              {b.hotel?.address
                ? <div className="muted det-hotel-addr">{b.hotel.address}</div>
                : b.hotel?.city ? <div className="muted det-hotel-addr">{b.hotel.city}</div> : null}
            </div>
            <div className="det-grid">
              <Row label="Room">{b.stay?.roomName || b.rooms?.[0]?.name}</Row>
              <Row label="Check In">{day(b.stay?.checkIn)}{b.hotel?.checkInTime ? ` · ${b.hotel.checkInTime}` : ''}</Row>
              <Row label="Check Out">{day(b.stay?.checkOut)}{b.hotel?.checkOutTime ? ` · ${b.hotel.checkOutTime}` : ''}</Row>
              <Row label="Nights">{b.stay?.nights}</Row>
              <Row label="Rooms">{b.stay?.rooms}</Row>
              <Row label="Guests">{b.stay?.guests}</Row>
            </div>

            {/* Rooms & Guests */}
            {(b.rooms || []).length ? (
              <>
                <h4 className="det-h">Rooms &amp; Guests</h4>
                {b.rooms.map((r, i) => (
                  <div className="det-leg" key={i}>
                    <div className="det-leg-head">
                      <b>Room {i + 1} · {r.name || 'Room'}</b>
                      <span className="muted"> · {r.mealBasis || 'Room Only'} · {r.adults || 0} Adult{(r.adults || 0) > 1 ? 's' : ''}{r.children ? ` · ${r.children} Child` : ''}</span>
                    </div>
                    {(r.travellers || []).length ? (
                      <ul className="det-pax">
                        {/* Name + pax type only — PAN is deliberately NOT shown
                            here (client rule, 2026-09-10): it lives on the
                            invoice and the checkout's PAN card. It was also
                            misleading, since the API defaults the LEAD's PAN
                            onto every traveller the supplier echoes, so room 2's
                            own PAN read as room 1's. */}
                        {r.travellers.map((t, j) => (
                          <li key={j}>{guestName(t, j)}<span className="muted"> · {(t.type || 'ADULT') === 'CHILD' ? 'Child' : 'Adult'}</span></li>
                        ))}
                      </ul>
                    ) : <div className="muted">No guest records echoed by the supplier.</div>}
                  </div>
                ))}
              </>
            ) : null}

            {/* Lead contact */}
            <h4 className="det-h">Lead Guest &amp; Contact</h4>
            <div className="det-grid">
              <Row label="Lead Guest">{b.lead_guest ? [b.lead_guest.firstName, b.lead_guest.lastName].filter(Boolean).join(' ') : (b.rooms?.[0]?.travellers?.[0] ? guestName(b.rooms[0].travellers[0], 0) : null)}</Row>
              <Row label="Email">{b.contact?.email}</Row>
              <Row label="Mobile">{b.contact?.phone}</Row>
            </div>

            {/* Fare */}
            <h4 className="det-h">Price Summary</h4>
            <div className="det-fare">
              {/* Base fare INCLUDES our processing fee (no line of its own). */}
              {feeLine('Base fare', (Number(fare.base_fare) || 0) + (Number(fare.processing_fee) || 0), true)}
              {feeLine('Taxes & fees', fare.gst_amount, true)}
              {feeLine(fare.convenience_label || 'Convenience fee', fare.convenience_fee, true)}
              {feeLine('Transaction fee', fare.transaction_fee)}
              {Number(fare.coupon_discount)
                ? <div className="det-fare-row"><span>{fare.discount_label || `Discount${fare.coupon_code ? ` (${fare.coupon_code})` : ''}`}</span><span>− {inr2(fare.coupon_discount)}</span></div>
                : null}
              <div className="det-fare-row"><span><b>Total paid</b></span><span><b>{inr2(fare.total_paid)}</b></span></div>
              {/* How that total was actually settled — shown only when the Neon
                  Wallet paid some of it; a card-only stay reads as before. */}
              {Number(fare.wallet_paid) > 0 ? (
                <>
                  <div className="det-fare-row"><span>↳ from wallet</span><span>{inr2(fare.wallet_paid)}</span></div>
                  <div className="det-fare-row"><span>↳ from gateway</span><span>{inr2(fare.gateway_paid)}</span></div>
                </>
              ) : null}
            </div>

            {/* Cancellation / refund (only once relevant) */}
            {(b.refund?.requested_at || b.refund?.reason) ? (
              <>
                <h4 className="det-h">Cancellation</h4>
                <div className="det-grid">
                  <Row label="Requested">{dt(b.refund.requested_at)}</Row>
                  <Row label="Reason">{b.refund.reason}</Row>
                </div>
              </>
            ) : null}

            {/* Refund workflow (CR E1) — stepper + history + admin transitions. */}
            <RefundPipeline
              kind="hotel"
              bookingId={b.id}
              refund={b.refund}
              bookingStatus={b.booking_status}
              detailsQueryKey={['hotel-booking-details', bookingId]}
            />

            {/* Amendments (flight parity) */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 16 }}>
              <h4 className="det-h" style={{ margin: 0 }}>Amendments</h4>
              <button className="btn ghost" onClick={() => setRaiseOpen(true)}>
                <Icon name="plus" /> Raise Amendment
              </button>
            </div>
            {amendmentsQ.isLoading ? <div className="muted">Loading amendments…</div>
              : amendments.length === 0 ? <div className="muted">No amendments raised on this booking.</div>
              : (
              <table className="table det-pax">
                <thead><tr><th>Type</th><th>Status</th><th>Raised by</th><th>On</th></tr></thead>
                <tbody>
                  {amendments.map((a) => (
                    <tr key={a.amendment_id}>
                      <td>{prettyType(a.type)}</td>
                      <td><Chip tone={/reject|fail/i.test(a.status) ? 'rej' : /success/i.test(a.status) ? 'ok' : 'pend'}>{a.status}</Chip></td>
                      <td>{a.created_by || '—'}</td>
                      <td>{dt(a.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Timeline */}
            <BookingTimeline kind="hotel" bookingId={b.id} />

            {/* Invoice + Voucher both render INSIDE the console (admin-native doc
                pages), never a redirect to the customer site. Which ones exist is
                the docAccess() rule: nothing while pending or cancellation-
                pending, everything when confirmed, invoice only when cancelled. */}
            <div className="form-actions" style={{ flexWrap: 'wrap', gap: 8 }}>
              {docs.invoice ? (
                <Link className="btn ghost" to={docPath(`/hotel/bookings/${b.id}/invoice`, audience)} onClick={onClose}>
                  <Icon name="download" /> Tax Invoice
                </Link>
              ) : null}
              {docs.voucher ? (
                <Link className="btn ghost" to={docPath(`/hotel/bookings/${b.id}/voucher`, audience)} onClick={onClose}>
                  <Icon name="page" /> Voucher
                </Link>
              ) : null}
              {docs.share ? (
                <button className="btn ghost" onClick={() => setShareOpen(true)}>
                  <Icon name="mail" /> Share by Email
                </button>
              ) : null}
              {onUpdate ? <button className="btn ghost" onClick={() => onUpdate(b)}><Icon name="edit" /> Update</button> : null}
              {canCancel && onCancel ? (
                <button className="btn danger" onClick={() => onCancel(b)}><Icon name="x" /> Cancel booking</button>
              ) : null}
            </div>
          </div>
        )}
        </div>
      </div>
    </div>

    {raiseOpen && b && (
      <RaiseHotelAmendmentsModal
        booking={b}
        onClose={() => setRaiseOpen(false)}
        onCancellation={(bk) => onCancel?.(bk)}
        onRaised={() => amendmentsQ.refetch()}
      />
    )}

    {shareOpen && b && (
      <ShareEmailModal kind="hotel" booking={b} onClose={() => setShareOpen(false)} />
    )}
    </>
  );
}
